import { useCallback, useState } from 'react';
import { influencer } from '@round/api';
import { getCreatorSelectionId, getCreatorSelectionIds } from './helpers';

/**
 * Selection for the drawer's rows, held outside the table so it survives refetches, paging and changes
 * to the search and filters.
 *
 * Keyed on the creator rather than the account: adding a creator adds every platform account they have,
 * so an account is never selected on its own.
 *
 * Holds ids rather than the records they were selected from. A record carries the pool membership it had
 * when its page was fetched, which goes stale as creators are added, so the accounts to post are read
 * from current data at add time instead.
 */
export function useCreatorSelection() {
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<Set<string>>(() => new Set());

    const toggleCreator = (creator: influencer.CreatorSearchResult) => {
        const creatorId = getCreatorSelectionId(creator);
        if (!creatorId) {
            return;
        }

        setSelectedCreatorIds((previous) => {
            const next = new Set(previous);
            if (next.has(creatorId)) {
                next.delete(creatorId);
            } else {
                next.add(creatorId);
            }

            return next;
        });
    };

    // Adds to the selection rather than replacing it: the rows on screen are one filtered view of a
    // selection that spans them all.
    const selectCreators = (creators: influencer.CreatorSearchResult[]) => {
        setSelectedCreatorIds((previous) => new Set([...previous, ...getCreatorSelectionIds(creators)]));
    };

    const deselectCreators = (creators: influencer.CreatorSearchResult[]) => {
        setSelectedCreatorIds((previous) => {
            const next = new Set(previous);
            getCreatorSelectionIds(creators).forEach((creatorId) => next.delete(creatorId));

            return next;
        });
    };

    // Stable and a no-op when already empty: the drawer clears this from an effect, which would
    // otherwise re-run on every render and set a fresh Set each time.
    const clearSelection = useCallback(() => {
        setSelectedCreatorIds((previous) => (previous.size ? new Set() : previous));
    }, []);

    return {
        selectedCreatorIds,
        toggleCreator,
        selectCreators,
        deselectCreators,
        clearSelection,
    };
}
